/**
 * Pure schedule-readiness checks for Game Pass seasons.
 *
 * Before a draft season can be scheduled it must be structurally complete:
 *   1. It holds at least one challenge.
 *   2. Every challenge holds at least one task.
 *
 * When a challenge has no task we surface the first offending challenge by name
 * so the admin knows exactly where to add a task — rather than listing every
 * empty challenge at once.
 *
 * Kept framework-free (no React) so it can be unit-tested in isolation and
 * reused by any surface that schedules a season.
 */

import { AppStrings } from "../../utils/constants/AppStrings";
import type { ChallengeDraft } from "../../components/GamePass/CreateSeason/createSeasonData";

/** The kind of readiness problem found, used to build the user-facing copy. */
export type SeasonScheduleReadinessIssue =
  | { type: "NO_CHALLENGES" }
  | { type: "CHALLENGE_NO_TASKS"; challengeName: string };

/**
 * Returns the first readiness issue that blocks scheduling, or `null` when the
 * season is ready. The check is short-circuited at the first failure so a
 * single empty challenge is reported rather than every one.
 */
export const findSeasonScheduleReadinessIssue = (
  challenges: ReadonlyArray<ChallengeDraft>,
): SeasonScheduleReadinessIssue | null => {
  if (challenges.length === 0) {
    return { type: "NO_CHALLENGES" };
  }

  const emptyChallenge = challenges.find(
    (challenge) => challenge.tasks.length === 0,
  );
  if (emptyChallenge) {
    return {
      type: "CHALLENGE_NO_TASKS",
      challengeName: emptyChallenge.name?.trim() ?? "",
    };
  }

  return null;
};

/**
 * Build the user-facing error message for a detected readiness issue. Names the
 * empty challenge so the admin knows where to add a task; falls back to a
 * generic message when the challenge has no resolvable name.
 */
export const buildSeasonScheduleReadinessMessage = (
  issue: SeasonScheduleReadinessIssue,
): string => {
  if (issue.type === "NO_CHALLENGES") {
    return AppStrings.SEASON_SCHEDULE_NO_CHALLENGES;
  }

  const name = issue.challengeName.trim();
  if (!name) {
    return AppStrings.SEASON_SCHEDULE_CHALLENGE_NO_TASKS_GENERIC;
  }
  return AppStrings.SEASON_SCHEDULE_CHALLENGE_NO_TASKS.replace(
    "{challengeName}",
    name,
  );
};
